/**
 * <cart-drawer> — side cart drawer (extends DrawerComponent).
 * Loaded from `sections/main-cart_drawer.liquid`.
 */

import { Delegate } from '@theme/delegate';
import { DrawerComponent } from '@theme/drawer-component';
import { AddToCart, buildCartQuantityUpdate } from '@theme/cart-api';
import { getVisibleCartCount, renderHeaderCartCount } from '@theme/cart-visible-count';
import { responsive } from '@theme/responsive';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';

const CART_FETCH_HEADERS = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

const CART_TIMINGS = {
  REMOVE_ANIMATION_MS: 350,
  SCROLL_LOCK_DELAY_MS: 500,
  LAYOUT_RECALC_DELAY_MS: 100,
  CONTENT_SETTLE_DELAY_MS: 200,
  LOADER_ANIMATION_MS: 550,
  SECTION_SYNC_DELAY_MS: 150,
};

const SECTION_SYNC_ATTEMPTS = 3;

class CartDrawerComponent extends DrawerComponent {
  static #boundInstances = new WeakSet();

  #delegateElement = null;
  #delegateBody = null;
  #options = {};
  #loader = null;
  #pageLoader = null;
  #announcer = null;
  #a11yMessages = {};
  #announceTimer = null;
  #scrollLockDelayTimer = null;
  #shippingProgressRaf1 = null;
  #shippingProgressRaf2 = null;
  #isUpdating = false;
  #recAbortController = null;
  #recsSnapshot = null;
  #pendingRecsReload = false;
  #uspCache = null;
  #recommendationLoading = false;
  #loadingRecosAfterAdd = false;
  #recommendationAtcLoaderTimer = null;
  #onRecommendationAtcCapture = null;

  #onCartOpenEvent = () => {
    this.open();
  };

  #onSearchOpening = () => {
    if (this.isOpen) {
      this.close();
    }
  };

  onBeforeOpen() {
    document.dispatchEvent(
      new CustomEvent('cart-drawer:opening', {
        bubbles: true,
        detail: { drawerId: this.id },
      })
    );
  }

  onBeforeClose() {
    // Cart content is re-rendered in place, so the scrollable node can be replaced
    // while the drawer stays open. Ensure the currently mounted node is unlocked.
    const scrollable = this.querySelector('[data-scrollable]');
    if (scrollable) {
      enableBodyScroll(scrollable);
    }
  }

  connectedCallback() {
    if (this.hasAttribute('data-no-drawer')) return;

    super.connectedCallback();

    this.#options = this.#parseOptions();
    this.#loader = this.querySelector('.item-loader');
    this.#pageLoader = document.body.querySelector('.page-loader');
    this.#announcer = this.querySelector('.cart-drawer__announcer');
    this.#a11yMessages = {
      added: this.dataset.announceAdded || 'Item added to cart',
      removed: this.dataset.announceRemoved || 'Item removed from cart',
      quantity: this.dataset.announceQuantity || 'Cart quantity updated',
      error: this.dataset.announceError || 'Failed to update cart. Please try again.',
    };

    new AddToCart(this);
    this.#bindEvents();
    this.#seedUspCacheFromTemplate();

    if ((this.#options.itemCount ?? 0) > 0) {
      this.#restoreUspMarkup();
    }

    this.#updateUspCache();

    this.#onRecommendationAtcCapture = this.#handleRecommendationAtcCapture.bind(this);
    this.addEventListener('click', this.#onRecommendationAtcCapture, true);
  }

  disconnectedCallback() {
    const scrollable = this.querySelector('[data-scrollable]');
    if (scrollable) {
      enableBodyScroll(scrollable);
    }

    super.disconnectedCallback();
    if (this.#announceTimer) {
      clearTimeout(this.#announceTimer);
      this.#announceTimer = null;
    }
    this.#clearScrollLockDelayTimer();
    this.#clearRecommendationAtcLoaderTimer();
    if (this.#onRecommendationAtcCapture) {
      this.removeEventListener('click', this.#onRecommendationAtcCapture, true);
      this.#onRecommendationAtcCapture = null;
    }
    document.removeEventListener('product:added', this.#onProductAdded);
    document.removeEventListener('cart:item-added', this.#onProductAdded);
    document.removeEventListener('cart-drawer:open', this.#onCartOpenEvent);
    document.removeEventListener('search:opening', this.#onSearchOpening);
    this.#delegateElement?.off?.();
    this.#delegateBody?.off?.();
    CartDrawerComponent.#boundInstances.delete(this);
  }

  #parseOptions() {
    try {
      return JSON.parse(this.getAttribute('data-settings') ?? '{}');
    } catch {
      return {};
    }
  }

  #bindEvents() {
    if (CartDrawerComponent.#boundInstances.has(this)) return;

    document.addEventListener('cart-drawer:open', this.#onCartOpenEvent);
    document.addEventListener('search:opening', this.#onSearchOpening);
    document.addEventListener('product:added', this.#onProductAdded);
    document.addEventListener('cart:item-added', this.#onProductAdded);

    this.#delegateElement = new Delegate(this);
    this.#delegateElement.on(
      'click',
      '[data-action="update-item-quantity"], [data-action="remove-item"]',
      this.#updateItemQuantity
    );
    this.#delegateElement.on(
      'change',
      '[data-action="update-change-quantity"]',
      this.#updateItemQuantity
    );
    this.#delegateElement.on(
      'quantity:changed',
      '.QuantitySelector__CurrentQuantity, .cart-item__quantity',
      this.#updateItemQuantity
    );

    this.#delegateBody = new Delegate(document.body);
    this.#delegateBody.on(
      'mouseover',
      '.header-link-list.header-main-menu-items.set-left .header-link-desk',
      this.#onDesktopMenuHoverClose
    );

    CartDrawerComponent.#boundInstances.add(this);
  }

  #onDesktopMenuHoverClose = (event) => {
    if (!responsive.matchesBreakpoint('lap_and_up')) return;
    if (!this.isOpen) return;

    const menuItem = event.target.closest('.header-link-desk');
    if (!menuItem || menuItem.contains(event.relatedTarget)) return;

    this.close();
  };

  #fetchCart() {
    return fetch(`${window.routes.localeRootUrl}/cart.js?t=${Date.now()}`, {
      credentials: 'same-origin',
      headers: CART_FETCH_HEADERS,
      cache: 'no-store',
    });
  }

  #postCart(endpoint, body) {
    return fetch(`${window.routes.localeRootUrl}${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: CART_FETCH_HEADERS,
      body: JSON.stringify(body),
    });
  }

  #announceToScreenReader(message) {
    if (!this.#announcer || !message) return;
    if (this.#announceTimer) {
      clearTimeout(this.#announceTimer);
      this.#announceTimer = null;
    }

    this.#announcer.textContent = '';
    requestAnimationFrame(() => {
      this.#announcer.textContent = message;
      this.#announceTimer = setTimeout(() => {
        this.#announcer.textContent = '';
      }, 1000);
    });
  }

  async #showCartUpdateError(target, response = null, fallbackContent = {}) {
    let content = fallbackContent;
    try {
      content = response ? await response.json() : fallbackContent;
    } catch (error) {
      console.error('Cart update: Failed to parse error response', error);
    }

    const message = AddToCart.getCartErrorMessage(content);
    AddToCart.showStatusToast(message, target);
    this.#announceToScreenReader(message);
  }

  open() {
    super.open();
    this.#scheduleCartDrawerFocusTarget();
  }

  #scheduleCartDrawerFocusTarget() {
    const duration =
      Number.parseFloat(
        getComputedStyle(this).getPropertyValue('--drawer-transition-duration')
      ) || 400;

    window.setTimeout(() => {
      if (!this.isOpen) return;

      const closeBtn = this.querySelector('.cart-drawer__close');
      const scrollable = this.querySelector('[data-scrollable]');

      if (closeBtn && document.activeElement === closeBtn) {
        closeBtn.blur();
      }

      if (scrollable) {
        scrollable.setAttribute('tabindex', '-1');
        scrollable.focus({ preventScroll: true });
      }
    }, duration + 10);
  }

  onOpened() {
    this.#clearScrollLockDelayTimer();
    if (this.getAttribute('aria-expanded') === 'false') {
      this.#scrollLockDelayTimer = setTimeout(() => {
        this.#scrollLockDelayTimer = null;
        if (!this.isOpen || this.getAttribute('aria-expanded') !== 'true') return;
        if (!this.hasAttribute('data-open-drawer')) return;

        const focusElement = this.querySelector('[data-scrollable]');
        if (focusElement) {
          disableBodyScroll(focusElement, bodyScrollLockOptions);
        }
      }, CART_TIMINGS.SCROLL_LOCK_DELAY_MS);
    }

    this.#updateLayoutVariables();

    if (this.#options.showRecommendations && this.#shouldReloadRecommendations()) {
      this.#loadRecommendations();
    }
  }

  #clearScrollLockDelayTimer() {
    if (!this.#scrollLockDelayTimer) return;
    clearTimeout(this.#scrollLockDelayTimer);
    this.#scrollLockDelayTimer = null;
  }

  #updateLayoutVariables() {
    const header = this.querySelector('.cart-drawer__header');
    const footer = this.querySelector('.cart-drawer__footer');
    const shippingZone =
      this.querySelector('.cart-drawer__shipping-zone') ||
      this.querySelector('.cart-drawer__shipping');

    document.documentElement.style.setProperty(
      '--cart-drawer__header-height',
      `${header?.offsetHeight ?? 0}px`
    );
    document.documentElement.style.setProperty(
      '--cart-drawer__footer-height',
      `${footer?.offsetHeight ?? 0}px`
    );
    document.documentElement.style.setProperty(
      '--cart-drawer__shipping-height',
      `${shippingZone?.offsetHeight ?? 0}px`
    );
  }

  #updateItemQuantity = async (event, target) => {
    event.preventDefault();
    if (this.#isUpdating) return;

    const cartItem = target.closest('.cartItem, .cart-item');
    const itemLoader = cartItem?.querySelector('.item-loader');
    if (!cartItem || !document.body.contains(cartItem)) return;

    const quantity =
      target.tagName === 'INPUT'
        ? Math.max(parseInt(target.value, 10) || 0, 0)
        : parseInt(target.getAttribute('data-quantity'), 10);

    const lineId = target.getAttribute('data-line-id');
    if (!lineId) return;

    const isRemove = quantity === 0;
    if (isRemove) {
      this.#showCartLoader();
    } else {
      itemLoader?.classList.add('is-visible');
    }

    this.#isUpdating = true;
    try {
      const cartRes = await this.#fetchCart();
      const cart = cartRes?.ok ? await cartRes.json() : null;

      const { endpoint, body } = buildCartQuantityUpdate(cart, lineId, quantity);

      const response = await this.#postCart(endpoint, body);
      if (!response.ok) {
        itemLoader?.classList.remove('is-visible');
        if (isRemove) this.#hideCartLoader();
        await this.#showCartUpdateError(target, response);
        return;
      }

      const data = await response.json();

      if (
        window.theme.cartMode === 'page' &&
        window.location.pathname === window.routes.cart_page_url
      ) {
        window.location.reload();
        return;
      }

      if (data.item_count === 0) {
        await this.#renderCart();
        this.#announceToScreenReader(this.#a11yMessages.removed);
      } else if (quantity === 0) {
        const nextItem = cartItem.nextElementSibling;
        cartItem.classList.add('removing');
        setTimeout(() => {
          cartItem.remove();
          if (nextItem) {
            nextItem.classList.add('repo--top');
            setTimeout(
              () => nextItem.classList.remove('repo--top'),
              CART_TIMINGS.REMOVE_ANIMATION_MS
            );
          }
        }, CART_TIMINGS.REMOVE_ANIMATION_MS);
        await this.#renderCart();
        this.#announceToScreenReader(this.#a11yMessages.removed);
      } else {
        await this.#renderCart();
        this.#announceToScreenReader(this.#a11yMessages.quantity);
      }
    } catch (err) {
      console.error('Failed to update item quantity:', err);
      itemLoader?.classList.remove('is-visible');
      if (isRemove) this.#hideCartLoader();
      await this.#showCartUpdateError(target);
    } finally {
      this.#isUpdating = false;
      itemLoader?.classList.remove('is-visible');
    }
  };

  #showCartLoader() {
    this.#loader?.classList.add('is-visible');
  }

  #hideCartLoader() {
    this.#loader?.classList.remove('is-visible');
  }

  #clearRecommendationAtcLoaderTimer() {
    if (!this.#recommendationAtcLoaderTimer) return;
    clearTimeout(this.#recommendationAtcLoaderTimer);
    this.#recommendationAtcLoaderTimer = null;
  }

  #handleRecommendationAtcCapture(event) {
    if (!this.isOpen) return;

    const atcButton = event.target.closest('.cart-drawer__recommendations [data-action="atc"]');
    if (!atcButton || atcButton.hasAttribute('disabled') || atcButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.#showCartLoader();
    this.#clearRecommendationAtcLoaderTimer();
    this.#recommendationAtcLoaderTimer = setTimeout(() => {
      this.#recommendationAtcLoaderTimer = null;
      this.#hideCartLoader();
    }, 10000);
  }

  #onProductAdded = async () => {
    if (
      window.theme.cartMode === 'page' ||
      window.location.pathname === window.routes.cart_page_url
    ) {
      return;
    }

    this.#clearRecommendationAtcLoaderTimer();

    if (this.isOpen || this.hasAttribute('data-open-drawer')) {
      this.#showCartLoader();
    }

    this.#loadingRecosAfterAdd = true;

    await this.#renderCart();
    this.#announceToScreenReader(this.#a11yMessages.added);

    this.#loadingRecosAfterAdd = false;

    if (!this.hasAttribute('data-open-drawer')) return;
    if (this.isOpen) return;

    this.open();
  };

  async #syncOptionsFromCart() {
    try {
      const cartRes = await this.#fetchCart();
      if (!cartRes.ok) return null;
      const cartData = await cartRes.json();
      this.#applyCartDataToOptions(cartData);
      return cartData;
    } catch {
      return null;
    }
  }

  #applyCartDataToOptions(cartData) {
    if (!cartData) return;

    const itemCount = cartData.item_count ?? 0;
    this.#options.itemCount = itemCount;
    this.#options.productId =
      cartData.items?.length > 0 ? cartData.items[0].product_id : null;

    if (itemCount === 0) {
      this.setAttribute('data-empty', 'true');
    } else {
      this.removeAttribute('data-empty');
    }

    this.#syncShowRecommendations();
  }

  #syncShowRecommendations() {
    const collection = this.getAttribute('data-recommendation-collection')?.trim() || '';
    const itemCount = this.#options.itemCount ?? 0;

    if (this.#options.showRecommendations === false) return;

    this.#options.showRecommendations = itemCount > 0 || collection !== '';
  }

  async #fetchCartDrawerSectionHtml() {
    const sectionUrl = `${window.routes.localeRootUrl}/?section_id=main-cart_drawer&t=${Date.now()}`;
    const content = await fetch(sectionUrl, {
      credentials: 'same-origin',
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    });

    if (!content.ok) {
      throw new Error(`HTTP error: ${content.status}`);
    }

    return content.text();
  }

  #getSectionItemCount(tempElement) {
    try {
      const cartEl =
        tempElement.querySelector('#cart') ||
        tempElement.querySelector('#cart-page') ||
        tempElement.querySelector('cart-drawer');
      const settings = JSON.parse(cartEl?.getAttribute('data-settings') ?? '{}');
      return settings.itemCount ?? 0;
    } catch {
      return null;
    }
  }

  async #fetchSyncedCartDrawerHtml(cartData) {
    let cartCount = cartData?.item_count ?? 0;
    let html = await this.#fetchCartDrawerSectionHtml();

    for (let attempt = 0; attempt < SECTION_SYNC_ATTEMPTS; attempt += 1) {
      const tempCheck = document.createElement('div');
      tempCheck.innerHTML = html;
      const sectionCount = this.#getSectionItemCount(tempCheck);

      if (sectionCount == null || sectionCount === cartCount) {
        return html;
      }

      if (sectionCount > cartCount) {
        const retryCartRes = await this.#fetchCart();
        if (retryCartRes.ok) {
          cartData = await retryCartRes.json();
          cartCount = cartData.item_count ?? 0;
        }
      }

      if (attempt < SECTION_SYNC_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, CART_TIMINGS.SECTION_SYNC_DELAY_MS));
        html = await this.#fetchCartDrawerSectionHtml();
      }
    }

    return html;
  }

  async #renderCart() {
    if (
      window.theme.cartMode === 'page' &&
      window.location.pathname === window.routes.cart_page_url
    ) {
      window.location.reload();
      return;
    }

    if (this.#pageLoader) this.#pageLoader.style.width = '90%';

    try {
      const cartRes = await this.#fetchCart();
      const cartData = cartRes.ok ? await cartRes.json() : null;
      const html = await this.#fetchSyncedCartDrawerHtml(cartData);

      this.#replaceContent(html, cartData);
    } catch (error) {
      console.error('Failed to render cart:', error);
      if (this.#pageLoader) this.#pageLoader.style.width = '0%';
      this.#hideCartLoader();
      this.#announceToScreenReader(this.#a11yMessages.error);
    }
  }

  #replaceContent(html, cartData = null) {
    if (this.#pageLoader) this.#pageLoader.style.width = '100%';

    setTimeout(() => {
      if (this.#pageLoader) {
        this.#pageLoader.style.left = 'unset';
        this.#pageLoader.style.right = '0px';
        this.#pageLoader.style.width = '0%';
        setTimeout(() => {
          this.#pageLoader.style.left = '0px';
          this.#pageLoader.style.right = 'unset';
        }, CART_TIMINGS.LOADER_ANIMATION_MS);
      }
    }, CART_TIMINGS.LAYOUT_RECALC_DELAY_MS);

    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    const prevRecsSnapshot = this.#recsSnapshot ?? this.#buildRecsSnapshot();

    try {
      const cartEl =
        tempElement.querySelector('#cart') ||
        tempElement.querySelector('#cart-page') ||
        tempElement.querySelector('cart-drawer');
      this.#options = JSON.parse(cartEl?.getAttribute('data-settings') ?? '{}');
      this.#syncCartDrawerAttributes(cartEl);
    } catch {
      this.#options = {};
    }

    if (cartData) {
      this.#applyCartDataToOptions(cartData);
    } else {
      this.#syncShowRecommendations();
    }

    const nextRecsSnapshot = this.#buildRecsSnapshotFromOptions(
      this.#options,
      tempElement
    );
    const cartEmptied =
      prevRecsSnapshot.mode === 'full' && nextRecsSnapshot.mode === 'empty';
    const cartFilled =
      prevRecsSnapshot.mode === 'empty' && nextRecsSnapshot.mode === 'full';
    const recsModeChanged = prevRecsSnapshot.mode !== nextRecsSnapshot.mode;
    this.#pendingRecsReload =
      recsModeChanged ||
      this.#shouldReloadRecommendations(nextRecsSnapshot, prevRecsSnapshot);

    const itemCount = this.#options.itemCount ?? 0;
    const visibleCount = cartData
      ? getVisibleCartCount(cartData)
      : parseInt(itemCount, 10);
    window.theme = window.theme || {};
    window.theme.cartCount = visibleCount;
    renderHeaderCartCount(visibleCount);

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          item_count: parseInt(itemCount, 10),
          visible_count: visibleCount,
          product_ids: cartData
            ? [...new Set((cartData.items || []).map((item) => item.product_id))]
            : null,
        },
      })
    );

    const nextShippingPercent = this.#getShippingProgressPercent(
      tempElement.querySelector('.cart-drawer__shipping-progress-bar')
    );
    const previousShippingPercent = this.#getShippingProgressPercent(
      this.querySelector('.cart-drawer__shipping-progress-bar')
    );

    const incomingShippingBar = tempElement.querySelector(
      '.cart-drawer__shipping-progress-bar'
    );
    if (incomingShippingBar && Number.isFinite(previousShippingPercent)) {
      const safePrevious = Math.max(0, Math.min(previousShippingPercent, 100));
      incomingShippingBar.dataset.percent = safePrevious;
      incomingShippingBar.style.transition = 'none';
      incomingShippingBar.style.setProperty('--progress-scale', `${safePrevious / 100}`);
    }

    this.#replaceCartContent(tempElement, {
      preserveRecommendations: !this.#pendingRecsReload && !recsModeChanged,
      cartEmptied,
      cartFilled,
    });
    this.#updateCartFooter(tempElement, nextShippingPercent);
    this.#animateShippingProgressBar(nextShippingPercent);
    this.#fixIOSScroll();

    this.dispatchEvent(new CustomEvent('cart:render', { bubbles: true, detail: { item_count: itemCount } }));

    this.#postCartContentUpdate({
      pendingRecsReload: this.#pendingRecsReload,
      cartEmptied,
      cartFilled,
    });

    setTimeout(() => {
      if (this.isOpen) {
        const scrollable = this.querySelector('[data-scrollable]');
        if (scrollable) {
          enableBodyScroll(scrollable);
          disableBodyScroll(scrollable, bodyScrollLockOptions);
        }
      }
      this.#fixIOSScroll();
      this.#pendingRecsReload = false;
    }, CART_TIMINGS.CONTENT_SETTLE_DELAY_MS);
  }

  #syncCartDrawerAttributes(cartEl) {
    if (!cartEl) return;

    const settingsAttr = cartEl.getAttribute('data-settings');
    if (settingsAttr) this.setAttribute('data-settings', settingsAttr);

    if (cartEl.hasAttribute('data-empty')) {
      this.toggleAttribute('data-empty', cartEl.getAttribute('data-empty') === 'true');
    } else {
      this.removeAttribute('data-empty');
    }

    const productLimit = cartEl.getAttribute('product-limit');
    if (productLimit) this.setAttribute('product-limit', productLimit);

    const collectionHandle = cartEl.getAttribute('data-recommendation-collection');
    if (collectionHandle) {
      this.setAttribute('data-recommendation-collection', collectionHandle);
    }
  }

  async #syncRecommendationState() {
    const domItemCount = this.querySelectorAll('.cart-drawer__items .cart-item').length;
    if (domItemCount > 0) {
      const firstItem = this.querySelector('.cart-drawer__items .cart-item');
      const productId = parseInt(firstItem?.dataset.id, 10);
      if (Number.isInteger(productId) && productId > 0) {
        this.#options.itemCount = domItemCount;
        this.#options.productId = productId;
        return;
      }
    }

    try {
      const cartRes = await this.#fetchCart();
      if (!cartRes.ok) return;
      const cartData = await cartRes.json();
      this.#options.itemCount = cartData.item_count ?? 0;
      this.#options.productId =
        cartData.items?.length > 0 ? cartData.items[0].product_id : null;
    } catch {
      /* keep existing options */
    }
  }

  #initRecommendationCards(container) {
    container.querySelectorAll('product-card').forEach((card) => {
      customElements.upgrade?.(card);
    });
  }

  #revealRecommendations(container) {
    if (!container) return;

    container.removeAttribute('hidden');
    container.classList.remove('is-empty');
    container.classList.remove('animatedContent');
    container.removeAttribute('data-animation');
    container.removeAttribute('data-loaded');

    const duration = Number(window.theme?.animationDuration) || 400;
    const animationsOff =
      window.theme?.enableAnimations === false ||
      document.documentElement.classList.contains('animationsDisabled') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (animationsOff) {
      container.style.opacity = '';
      return;
    }

    container.style.opacity = '0';
    requestAnimationFrame(() => {
      const animation = container.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration,
        fill: 'forwards',
      });
      animation.finished
        .then(() => {
          container.style.opacity = '1';
        })
        .catch(() => {
          container.style.opacity = '1';
        });
    });
  }

  #uspHasItems(uspEl) {
    return !!uspEl?.querySelector(
      '.ann-bar__block-item, .cart-drawer__usp-bar__item, .cart-footer__usp-single'
    );
  }

  #seedUspCacheFromTemplate() {
    const template = document.getElementById('cart-drawer-usp-template');
    if (!(template instanceof HTMLTemplateElement)) return;

    const usp = template.content.querySelector('.cart-drawer__usp');
    if (this.#uspHasItems(usp)) {
      this.#uspCache = this.#cloneUspForCache(usp);
    }
  }

  #cloneUspForCache(uspEl) {
    const clone = uspEl.cloneNode(true);
    clone.querySelectorAll('[data-usp-clone="true"]').forEach((node) => node.remove());
    clone.querySelectorAll('[data-usp-clone-set="true"]').forEach((node) => node.remove());

    const uspSlider = clone.querySelector('cart-usps-slider');
    if (uspSlider) {
      const isStaticLayout = uspSlider.dataset.uspLayout === 'static';
      const animWrapper = uspSlider.querySelector('.pm__usps-slider--anim-wrapper');
      const duplicate = uspSlider.querySelector('.pm__usps-slider--duplicate-wrapper');

      if (!isStaticLayout) {
        if (animWrapper) {
          while (animWrapper.children.length > 1) {
            animWrapper.removeChild(animWrapper.lastChild);
          }
          animWrapper.style.cssText = '';
        }

        if (duplicate) {
          duplicate.style.cssText = '';
        }

        uspSlider.style.height = '';
        uspSlider.removeAttribute('data-usp-layout');
        uspSlider.removeAttribute('data-usp-count');
      }
    }

    return clone;
  }

  #updateUspCache() {
    const usp = this.querySelector('.cart-drawer__usp-mount .cart-drawer__usp');
    if (this.#uspHasItems(usp)) {
      this.#uspCache = this.#cloneUspForCache(usp);
    }
  }

  #getUspMarkupSource() {
    this.#seedUspCacheFromTemplate();
    if (this.#uspCache && this.#uspHasItems(this.#uspCache)) {
      return this.#uspCache;
    }
    return null;
  }

  #restoreUspMarkup() {
    const shippingZone = this.querySelector('.cart-drawer__shipping-zone');
    if (!shippingZone) return;

    const mount = shippingZone.querySelector('.cart-drawer__usp-mount');
    if (!mount) return;

    const existingUsp = mount.querySelector('.cart-drawer__usp');
    if (this.#uspHasItems(existingUsp)) {
      return;
    }

    const source = this.#getUspMarkupSource();
    if (!source) return;

    mount.replaceChildren(source.cloneNode(true));

    const slider = mount.querySelector('cart-usps-slider');
    if (typeof slider?.reinitialize === 'function') {
      requestAnimationFrame(() => {
        slider.reinitialize();
      });
    }
  }

  #postCartContentUpdate({
    pendingRecsReload = false,
    cartEmptied = false,
    cartFilled = false,
  } = {}) {
    const itemCount = this.#options.itemCount ?? 0;

    if (itemCount > 0) {
      this.#restoreUspMarkup();
      this.#updateUspCache();
      this.#updateLayoutVariables();
    }

    if (!(pendingRecsReload || cartEmptied || cartFilled)) return;
    if (!this.#options.showRecommendations) return;

    if (cartEmptied) {
      const container = this.querySelector('.cart-drawer__recommendations');
      if (container) {
        container.innerHTML = '';
        container.removeAttribute('hidden');
        container.classList.remove('is-empty');
      }
    }

    void this.#loadRecommendations({ force: true });
  }

  #buildRecsSnapshotFromOptions(options = this.#options, tempElement = null) {
    const itemCount = options.itemCount ?? 0;
    const hasItems = itemCount > 0;
    let cartProductIds = this.#getCartProductIds();

    if (tempElement) {
      const ids = tempElement
        .querySelector('#cart-drawer__form')
        ?.dataset.ids?.trim();
      if (ids) cartProductIds = ids;
    }

    return {
      mode: hasItems ? 'full' : 'empty',
      itemCount,
      productId: hasItems ? options.productId ?? null : null,
      cartProductIds,
      collection: this.getAttribute('data-recommendation-collection')?.trim() || '',
    };
  }

  #buildRecsSnapshot() {
    return this.#buildRecsSnapshotFromOptions();
  }

  #getCartProductIds() {
    const ids = this.querySelector('#cart-drawer__form')?.dataset.ids?.trim();
    return ids || '';
  }

  #hasRecommendationsContent() {
    const container = this.querySelector('.cart-drawer__recommendations');
    if (!container || container.hasAttribute('hidden')) return false;

    return !!container.querySelector(
      '.cart-drawer__recommendations-wrapper, .cart-drawer__rec-item'
    );
  }

  #shouldReloadRecommendations(nextSnapshot = null, prevSnapshot = null) {
    const current = nextSnapshot ?? this.#buildRecsSnapshot();
    const prev = prevSnapshot ?? this.#recsSnapshot;

    if (!prev) return true;
    if (prev.mode !== current.mode) return true;
    if (!this.#hasRecommendationsContent()) return true;

    if (current.mode === 'empty') return false;

    return (
      prev.cartProductIds !== current.cartProductIds || prev.productId !== current.productId
    );
  }

  async #loadRecommendations({ force = false } = {}) {
    const container = this.querySelector('.cart-drawer__recommendations');
    if (!container || !this.#options.showRecommendations) return;

    if (!force && !this.#shouldReloadRecommendations()) return;

    if (this.#recAbortController) {
      this.#recAbortController.abort();
    }
    this.#recAbortController = new AbortController();
    const { signal } = this.#recAbortController;

    this.#recommendationLoading = true;
    container.classList.remove('animatedContent', 'is-empty');
    container.removeAttribute('hidden');
    container.style.opacity = '';

    if (!force) {
      await this.#syncRecommendationState();
    } else {
      await this.#syncOptionsFromCart();
    }

    const baseUrl = window.routes.localeRootUrl;
    const itemCount = this.#options.itemCount ?? 0;
    const hasItems = itemCount > 0;
    const productId = this.#options.productId;
    const enabled = container.dataset.enabled !== 'false';
    const collection =
      this.getAttribute('data-recommendation-collection')?.trim() ||
      container.dataset.collection?.trim() ||
      '';
    const limit =
      this.getAttribute('product-limit') ||
      this.#options.recommendedLimit ||
      4;
    const intent = this.#options.intent || 'complementary';
    const cacheBust = `&t=${Date.now()}`;
    let url = null;

    if (hasItems) {
      container.dataset.recsSource = 'intent';
      container.removeAttribute('data-collection');

      if (!productId) {
        this.#recommendationLoading = false;
        return;
      }

      const collectionParam =
        enabled && collection ? `&collection=${encodeURIComponent(collection)}` : '';
      url = `${baseUrl}/recommendations/products?section_id=helper-recommendations&product_id=${productId}&limit=${limit}&intent=${encodeURIComponent(intent)}${collectionParam}${cacheBust}`;
    } else if (enabled && collection) {
      container.dataset.recsSource = 'collection';
      container.dataset.collection = collection;

      url = `${baseUrl}?section_id=helper-recommendations&collection=${encodeURIComponent(collection)}${cacheBust}`;
    } else {
      container.innerHTML = '';
      this.#recommendationLoading = false;
      return;
    }

    try {
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      const html = await response.text();
      const temp = document.createElement('div');
      temp.innerHTML = html;

      const upsell = temp.querySelector('.cart-drawer__upsell');
      const itemCount = upsell?.querySelectorAll('.cart-drawer__rec-item').length ?? 0;

      if (hasItems && (intent === 'complementary' || intent === 'related') && itemCount === 0) {
        container.innerHTML = '';
        container.hidden = true;
        return;
      }

      if (upsell && itemCount > 0) {
        container.innerHTML = upsell.innerHTML;
        container.style.opacity = '';
        this.#initRecommendationCards(container);
        this.#revealRecommendations(container);
        container.dispatchEvent(new CustomEvent('cart:render', { bubbles: true }));
        this.#recsSnapshot = this.#buildRecsSnapshot();
      } else {
        container.innerHTML = '';
        container.style.opacity = '';
        container.classList.remove('animatedContent');
        container.setAttribute('hidden', '');
        container.classList.add('is-empty');
        this.#recsSnapshot = this.#buildRecsSnapshot();
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[cart-drawer] loadRecommendations failed', err);
    } finally {
      this.#recommendationLoading = false;
    }
  }

  #updateCartFooter(tempElement, nextShippingPercent = null) {
    const itemCount = this.#options.itemCount ?? 0;
    if (itemCount > 0) {
      const footerParent = this.querySelector('.cart-drawer__footer');
      const newFooterTotals = tempElement.querySelector('.cart_drawer__footer-totals');
      const oldFooterTotals = this.querySelector('.cart_drawer__footer-totals');

      if (footerParent && newFooterTotals) {
        if (oldFooterTotals) {
          footerParent.replaceChild(newFooterTotals, oldFooterTotals);
        } else {
          footerParent.prepend(newFooterTotals);
        }
      }

      const newCheckoutSubmit = tempElement.querySelector('.cd__total-submit');
      const currentCheckoutSubmit = this.querySelector('.cd__total-submit');
      if (newCheckoutSubmit && currentCheckoutSubmit) {
        currentCheckoutSubmit.replaceWith(newCheckoutSubmit.cloneNode(true));
      }

      const currentShippingBar = this.querySelector('.cart-drawer__shipping-progress-bar');
      if (currentShippingBar && Number.isFinite(nextShippingPercent)) {
        currentShippingBar.dataset.percent = nextShippingPercent;
        if (Math.round(nextShippingPercent) >= 100) {
          currentShippingBar.classList.add('done');
          currentShippingBar.classList.remove('active');
        } else {
          currentShippingBar.classList.remove('done');
          currentShippingBar.classList.add('active');
        }
      }
    } else {
      this.#clearCartFooter(tempElement);
    }

    this.#updateLayoutVariables();
  }

  #getShippingProgressPercent(shippingBar) {
    if (!shippingBar) return null;
    const rawPercent = parseFloat(shippingBar.dataset.percent);
    if (Number.isNaN(rawPercent)) return null;
    return Math.max(0, Math.min(rawPercent, 100));
  }

  #animateShippingProgressBar(nextPercent) {
    if (!Number.isFinite(nextPercent)) return;

    const shippingBar = this.querySelector('.cart-drawer__shipping-progress-bar');
    if (!shippingBar) return;

    if (this.#shippingProgressRaf1) cancelAnimationFrame(this.#shippingProgressRaf1);
    if (this.#shippingProgressRaf2) cancelAnimationFrame(this.#shippingProgressRaf2);

    this.#shippingProgressRaf1 = requestAnimationFrame(() => {
      this.#shippingProgressRaf2 = requestAnimationFrame(() => {
        shippingBar.style.transition = 'transform 420ms ease';
        shippingBar.style.setProperty('--progress-scale', `${nextPercent / 100}`);
        this.#shippingProgressRaf1 = null;
        this.#shippingProgressRaf2 = null;
      });
    });
  }

  #fixIOSScroll() {
    const scrollContainer = this.querySelector('[data-scrollable]');
    if (!scrollContainer) return;

    const currentScroll = scrollContainer.scrollTop;
    scrollContainer.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      scrollContainer.style.overflow = '';
      scrollContainer.style.webkitOverflowScrolling = 'auto';
      requestAnimationFrame(() => {
        scrollContainer.style.webkitOverflowScrolling = 'touch';
      });
      scrollContainer.scrollTop = currentScroll;
    });
  }

  #clearCartFooter(tempElement) {
    if (!tempElement.querySelector('.cart-drawer__wrapper')) return;

    const wrapperParent = this.querySelector('.cart-drawer__wrapper');
    const newWrapper = tempElement.querySelector('.cart-drawer__wrapper');
    if (wrapperParent && newWrapper) {
      wrapperParent.innerHTML = newWrapper.innerHTML;
    }
  }

  #replaceCartContent(
    tempElement,
    { preserveRecommendations = false, cartEmptied = false, cartFilled = false } = {}
  ) {
    const currentWrapper = this.querySelector('.cart-drawer__wrapper');
    const newWrapper = tempElement.querySelector('.cart-drawer__wrapper');
    if (!currentWrapper || !newWrapper) return;

    const cartWrapperParent = currentWrapper.parentNode;
    const currentScrollable = currentWrapper.querySelector('[data-scrollable]');
    const currentScrollPosition = currentScrollable?.scrollTop ?? 0;

    if (currentScrollable) {
      enableBodyScroll(currentScrollable);
    }

    const currentRecs = currentWrapper.querySelector('.cart-drawer__recommendations');
    const shouldPreserveRecs =
      preserveRecommendations && this.#hasRecommendationsContent() && currentRecs;
    const preservedRecs = shouldPreserveRecs ? currentRecs.cloneNode(true) : null;

    cartWrapperParent.replaceChild(newWrapper, currentWrapper);

    if (preservedRecs) {
      const nextRecs = this.querySelector('.cart-drawer__recommendations');
      if (nextRecs) {
        nextRecs.replaceWith(preservedRecs);
      }
    }

    setTimeout(() => {
      this.#hideCartLoader();
    }, CART_TIMINGS.CONTENT_SETTLE_DELAY_MS);

    const nextScrollable = this.querySelector('.cart-drawer__wrapper [data-scrollable]');
    if (nextScrollable) {
      nextScrollable.scrollTop = currentScrollPosition;
    }

    setTimeout(() => {
      this.#updateLayoutVariables();
    }, CART_TIMINGS.LAYOUT_RECALC_DELAY_MS);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawerComponent);
}
